import React, { useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'
import { line, radius, shadow, font } from 'features/dashboard/tokens'
import SectionTitle from 'features/dashboard/components/SectionTitle'
import { scoreColour, formatPercent, scoreBand } from '../charts/scoreColour'

const TaskDomainDrawer = ({ open, domain, perBp = [], onClose }) => {
  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open || !domain) return null

  return (
    <>
      <Backdrop onClick={onClose} />
      <Drawer>
        <Head style={{ borderTop: `4px solid ${domain.colour || '#33becc'}` }}>
          <Title>
            <Eyebrow>Task domain</Eyebrow>
            <Name>{domain.name}</Name>
            {domain.freq_label ? <Freq>{domain.freq_label}</Freq> : null}
          </Title>
          <Close onClick={onClose} aria-label='Close'>
            <CloseIcon style={{ fontSize: 22 }} />
          </Close>
        </Head>

        {domain.description ? <Desc>{domain.description}</Desc> : null}

        <Stats>
          <Stat>
            <StatLabel>Score</StatLabel>
            <StatValue $colour={scoreColour(domain.score)}>{formatPercent(domain.score)}</StatValue>
            <StatHint>{scoreBand(domain.score)}</StatHint>
          </Stat>
          <Stat>
            <StatLabel>Building Passports</StatLabel>
            <StatValue>{domain.bp_count ?? 0}</StatValue>
          </Stat>
          <Stat>
            <StatLabel>Sets</StatLabel>
            <StatValue>{domain.set_count ?? 0}</StatValue>
          </Stat>
          <Stat>
            <StatLabel>Overdue</StatLabel>
            <StatValue $colour={domain.overdue ? '#b00020' : undefined}>{domain.overdue ?? 0}</StatValue>
          </Stat>
        </Stats>

        <SectionTitle>By Building Passport</SectionTitle>
        {perBp.length ? (
          <List>
            {perBp.map(bp => (
              <Row key={bp.building_passport_id}>
                <BpName>{bp.building_passport_name}</BpName>
                <BpScore $colour={scoreColour(bp.score)}>{formatPercent(bp.score)}</BpScore>
                {bp.overdue ? <Overdue>{bp.overdue} overdue</Overdue> : <OnTrack>on track</OnTrack>}
              </Row>
            ))}
          </List>
        ) : (
          <Empty>This domain isn't represented on any of your Building Passports yet.</Empty>
        )}
      </Drawer>
    </>
  )
}

export default TaskDomainDrawer

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12, 18, 41, 0.35);
  z-index: 1300;
`

const Drawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(440px, 92vw);
  background: ${({ theme }) => theme.colors.white};
  z-index: 1301;
  box-shadow: ${shadow.overlay};
  padding: 0 24px 24px;
  overflow-y: auto;
  animation: ${slideIn} 220ms cubic-bezier(.2,.7,.2,1);
`

const Head = styled.header`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 24px 24px 16px;
  border-bottom: 1px solid ${line.base};
  margin: 0 -24px;
`

const Title = styled.div`
  flex: 1;
`

const Eyebrow = styled.div`
  font-size: ${font.caption};
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 700;
  margin-bottom: 4px;
`

const Name = styled.h2`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.display};
  margin: 0;
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme.colors.haiti};
`

const Freq = styled.div`
  margin-top: 4px;
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Close = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.haiti};
  padding: 4px;
`

const Desc = styled.p`
  margin: 12px 0 0;
  font-size: ${font.body};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.haiti};
`

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin: 18px 0;
`

const Stat = styled.div`
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border-radius: ${radius.md};
  border: 1px solid ${line.base};
`

const StatLabel = styled.div`
  font-size: ${font.caption};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 600;
`

const StatValue = styled.div`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.display};
  letter-spacing: -0.8px;
  line-height: 1.1;
  color: ${({ $colour, theme }) => $colour || theme.colors.haiti};
`

const StatHint = styled.div`
  font-size: ${font.caption};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-top: 2px;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
`

const BpName = styled.div`
  font-weight: 600;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.haiti};
`

const BpScore = styled.div`
  font-weight: 700;
  font-size: ${font.body};
  color: ${({ $colour }) => $colour};
`

const Overdue = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #b00020;
  background: rgba(204,34,0,0.10);
  padding: 3px 8px;
  border-radius: ${radius.pill};
`

const OnTrack = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #33becc;
  background: rgba(51,190,204,0.10);
  padding: 3px 8px;
  border-radius: ${radius.pill};
`

const Empty = styled.div`
  padding: 14px;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.grey[800]};
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
`
